/**
 * Create / edit a page.
 *
 * The slug is chosen from the tenant's dashboard catalog, never typed: a page only does anything
 * if a real screen answers to its slug. On edit the slug is immutable — changing it would orphan
 * every announcement and tour already keyed to it.
 *
 * Switches here wait for Save, unlike the one on the card, which writes on toggle.
 *
 * @module Pages/BUs/Pages
 */

import { css } from '@emotion/css';
import { UTButton, UTModal, UTSelect, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { Page } from '../../Types/Pages.types';
import { getPagesForTenant } from '@/Shared/brand';
import { Switch } from '@/Shared/components/Switch/Switch';

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const hintStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const switchRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
`;

const switchTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const switchLabelStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const switchHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const warningStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--gl-yellow-dark);
  text-wrap: pretty;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

const validationStyle = css`
  flex: 1;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

export interface PageFormValues {
  tenantSlug: string;
  slug: string;
  name: string;
  isMain: boolean;
  isActive: boolean;
}

export interface PageFormModalProps {
  open: boolean;
  /** The page being edited, or `undefined` when creating. */
  page?: Page;
  /** Tenants to choose from, on create. */
  tenantOptions: { id: string; text: string; value: string }[];
  /** Every existing page, used to warn which one loses "Principal". */
  allPages: Page[];
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (values: PageFormValues) => void;
}

const EMPTY_VALUES: PageFormValues = {
  tenantSlug: '',
  slug: '',
  name: '',
  isMain: false,
  isActive: true,
};

export const PageFormModal = ({
  open,
  page,
  tenantOptions,
  allPages,
  isSaving,
  onClose,
  onSubmit,
}: PageFormModalProps): JSX.Element => {
  const isEditing = page !== undefined;
  const [values, setValues] = useState<PageFormValues>(EMPTY_VALUES);

  // Reload whenever the modal opens or switches to a different page.
  useEffect(() => {
    if (!open) return;

    setValues(
      page
        ? {
            tenantSlug: page.tenantSlug,
            slug: page.slug,
            name: page.name,
            isMain: page.isMain,
            isActive: page.isActive,
          }
        : EMPTY_VALUES,
    );
  }, [open, page]);

  /** Slugs already taken in this tenant, so the select cannot create a duplicate. */
  const takenSlugs = useMemo(
    () =>
      new Set(
        allPages
          .filter(p => p.tenantSlug === values.tenantSlug && p.id !== page?.id)
          .map(p => p.slug),
      ),
    [allPages, values.tenantSlug, page?.id],
  );

  const slugOptions = useMemo(
    () =>
      getPagesForTenant(values.tenantSlug)
        .filter(catalogPage => !takenSlugs.has(catalogPage.slug))
        .map(catalogPage => ({
          id: catalogPage.slug,
          text: `${catalogPage.label} (${catalogPage.slug})`,
          value: catalogPage.slug,
        })),
    [values.tenantSlug, takenSlugs],
  );

  const selectedCatalogPage = useMemo(
    () => getPagesForTenant(values.tenantSlug).find(p => p.slug === values.slug),
    [values.tenantSlug, values.slug],
  );

  /** The page that currently holds "Principal" and would lose it. */
  const currentMain = useMemo(
    () => allPages.find(p => p.tenantSlug === values.tenantSlug && p.isMain && p.id !== page?.id),
    [allPages, values.tenantSlug, page?.id],
  );

  const missing: string[] = [];
  if (!values.tenantSlug) missing.push('a business unit');
  if (!values.slug) missing.push('a tela do dashboard');
  if (!values.name.trim()) missing.push('o nome exibido');

  const isValid = missing.length === 0;

  const content = (
    <div className={formStyle}>
      {!isEditing && (
        <div className={fieldStyle}>
          <UTSelect
            options={tenantOptions}
            canSelectMultiple={false}
            hasCheckbox={false}
            inputOptions={{
              textFieldProps: {
                topLabel: 'Business unit',
                placeholder: 'Selecione a BU',
                required: true,
              },
            }}
            onSelectedOptionsChange={options => {
              const tenantSlug = options.length > 0 ? String(options[0].value) : '';
              // Changing tenant invalidates the slug: catalogs differ per brand.
              setValues(current => ({ ...current, tenantSlug, slug: '' }));
            }}
          />
        </div>
      )}

      <div className={fieldStyle}>
        {isEditing ? (
          <UTTextField
            topLabel='Tela do dashboard'
            value={values.slug}
            disabled
            onChange={() => undefined}
          />
        ) : (
          <UTSelect
            key={values.tenantSlug}
            options={slugOptions}
            canSelectMultiple={false}
            hasCheckbox={false}
            hasSearch
            inputOptions={{
              textFieldProps: {
                topLabel: 'Tela do dashboard',
                placeholder: values.tenantSlug
                  ? 'Selecione a tela'
                  : 'Escolha a business unit primeiro',
                required: true,
                disabled: !values.tenantSlug,
              },
            }}
            onSelectedOptionsChange={options => {
              setValues(current => ({
                ...current,
                slug: options.length > 0 ? String(options[0].value) : '',
              }));
            }}
          />
        )}
        {selectedCatalogPage ? (
          <span className={hintStyle}>
            {selectedCatalogPage.slug} · {selectedCatalogPage.route}
          </span>
        ) : null}
        {isEditing ? (
          <span className={switchHintStyle}>
            O slug não muda depois de criado — anúncios e tours já publicados apontam para ele.
          </span>
        ) : null}
      </div>

      <div className={fieldStyle}>
        <UTTextField
          topLabel='Nome exibido'
          required
          value={values.name}
          placeholder='Ex.: Projections'
          onChange={event => {
            setValues(current => ({ ...current, name: event.target.value }));
          }}
        />
      </div>

      <div className={switchRowStyle}>
        <Switch
          id='page-form-is-main'
          size='md'
          checked={values.isMain}
          aria-label='Página principal da BU'
          onChange={next => {
            setValues(current => ({ ...current, isMain: next }));
          }}
        />
        <div className={switchTextStyle}>
          <span className={switchLabelStyle}>Página principal da BU</span>
          <span className={switchHintStyle}>
            Recebe os anúncios criados sem página específica. Só uma por BU.
          </span>
          {values.isMain && currentMain ? (
            <span className={warningStyle}>
              {currentMain.name} deixa de ser a página principal.
            </span>
          ) : null}
        </div>
      </div>

      <div className={switchRowStyle}>
        <Switch
          id='page-form-is-active'
          size='md'
          checked={values.isActive}
          aria-label='Página ativa'
          onChange={next => {
            setValues(current => ({ ...current, isActive: next }));
          }}
        />
        <div className={switchTextStyle}>
          <span className={switchLabelStyle}>Página ativa</span>
          <span className={switchHintStyle}>
            Uma página inativa não entrega What&apos;s New nem tour.
          </span>
        </div>
      </div>

      <div className={footerStyle}>
        <span className={validationStyle}>{isValid ? '' : `Informe ${missing.join(', ')}.`}</span>
        <UTButton fitContent variant='tertiary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='page-form-submit'
          text={isSaving ? 'Salvando...' : 'Salvar'}
          disabled={!isValid || isSaving}
          onClick={() => {
            onSubmit(values);
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Editar página' : 'Criar página'}
      description={
        isEditing
          ? 'Altere o nome e o comportamento desta página.'
          : 'Aponte uma tela do dashboard para receber conteúdo do BFF.'
      }
      content={content}
      maxWidth='560px'
      hasButtonClose
      position='center'
    />
  );
};
